import { useState, useEffect, useRef } from "react"
import { Input, Modal, Button, Select, Spin, message, Avatar, Upload, Form, Image, Badge } from "antd"
import { SendOutlined, SearchOutlined, PlusOutlined, BellOutlined } from "@ant-design/icons"
import { doc, updateDoc } from "firebase/firestore"
import { useDocument } from "react-firebase-hooks/firestore"
import type { UploadFile } from "antd/es/upload/interface"
import { BlobServiceClient } from "@azure/storage-blob"
import type { IChatList, IChatDetail, INewChatUsers } from "../types/adminmessages"
import { chatCollectionModel } from "../firebase/firebase-config"
import FirestoreDatabaseService from "../firebase/firebase-service"
import { useChatMessages } from "../hooks/use-firebase-chat"
import { useFetchAdminChatList, useGetUsersForNewChat } from "../services/adminmessages"
import type IResponse from "../types/response"
import { storage } from "../utils/storage"
import { db } from "../firebase/firebase-service"
import { azureBlobStorageConfig } from "../helpers/config"
import { moveBlobInStorage } from "../helpers/blobhelper"
import { useGetSasToken } from '../services/blobcontext.service';
import { isNotNullUndefined } from "../helpers/common";
import { useAuth } from "../context/AuthContext";

export default function AdminMessages() {
  const [form] = Form.useForm();
  const [chatList, setChatList] = useState<IChatList[]>([])
  const [chatDetail, setChatDetail] = useState<IChatDetail[]>([])
  const [selectedChat, setSelectedChat] = useState<IChatList | null>(null)
  const [searchValue, setSearchValue] = useState("")
  const [searchNewUsersValue, setSearchNewUsersValue] = useState("")
  const [loading, setIsLoading] = useState(true)
  const [loadingAdminMessages, setLoadingAdminMessages] = useState(true)
  const chatDetailContainerRef = useRef<HTMLDivElement>(null)
  const [messageInput, setMessageInput] = useState("")
  const [currentUserId, setCurrentUserId] = useState<string>()
  const [selectedUserRole, setSelectedUserRole] = useState("traveller")
  const [showNewChatModal, setShowNewChatModal] = useState(false)
  const [newUsers, setNewUsers] = useState<INewChatUsers[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [loadingNewUsers, setLoadingNewUsers] = useState(true)
  const [creatingNewChat, setCreatingNewChat] = useState<string | null>(null)
  const [sasToken, setSasToken] = useState<string | null>(null);

  const [unreadFromOtherRole, setUnreadFromOtherRole] = useState<number>(0)

  // Image upload states
  const [uploadedImages, setUploadedImages] = useState<UploadFile[]>([])
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isSasLoaded, SetIsSasLoaded] = useState<boolean>(false);

  const { messages, loading: messagesLoading, error: messagesError } = useChatMessages(selectedChat?.chatId || null)
  const { hasPermission, permissionsLoaded, loadPermissions } = useAuth();

  useEffect(() => {
    
      loadPermissions();
    
  }, [permissionsLoaded]);

  const NewChatAdd = hasPermission("ADMIN_MESSAGES", "CREATE");
  const WriteMessage = hasPermission("ADMIN_MESSAGES", "WRITE");

  useEffect(() => {
    var userid = storage.getUserId()
    if (userid) setCurrentUserId(userid)
  }, [])

  // Real-time updates - only when not searching
  useEffect(() => {
    if (!isSearching) {
      // SAS token is only used to sign image URLs - it must not gate the chat list fetch
      getSasToken();

      const interval = setInterval(() => {
        fetchChatList("")
      }, 1500)

      return () => clearInterval(interval)
    }
  }, [selectedUserRole, isSearching])

  useEffect(() => {
    if (!isSearching && currentUserId) {
      const otherRole = selectedUserRole === "traveller" ? "attendant" : "traveller"
      fetchOtherRoleUnreadMessages(otherRole)

      const interval = setInterval(() => {

        fetchOtherRoleUnreadMessages(otherRole)
      }, 3000)

      return () => clearInterval(interval)
    }
  }, [selectedUserRole, isSearching, currentUserId])

  // Handle search with debouncing
  useEffect(() => {
    if (searchValue.trim()) {
      setIsSearching(true)
      const searchTimeout = setTimeout(() => {
        fetchChatList(searchValue)
      }, 500)

      return () => clearTimeout(searchTimeout)
    } else {
      // When search is cleared, get all data
      setIsSearching(false)
      fetchChatList("")
    }
  }, [searchValue])

  // Auto-select first chat when chat list changes
  useEffect(() => {
    if (chatList.length > 0 && !selectedChat) {
      setSelectedChat(chatList[0])
    } else if (chatList.length === 0) {
      setSelectedChat(null)
    } else if (selectedChat) {
      // Check if selected chat still exists in the list
      const stillExists = chatList.find((chat) => chat.chatId === selectedChat.chatId)
      if (!stillExists) {
        setSelectedChat(chatList.length > 0 ? chatList[0] : null)
      }
    }
  }, [chatList])

  useEffect(() => {
    if (creatingNewChat && chatList.length > 0) {
      const newestChat = chatList[0]
      if (newestChat && (!selectedChat || selectedChat.chatId !== newestChat.chatId)) {
        setSelectedChat(newestChat)
        setCreatingNewChat(null)
      }
    }
  }, [chatList, creatingNewChat, selectedChat])

  // Reset when role changes
  useEffect(() => {
    setSelectedChat(null)
    setSearchValue("")
    setIsSearching(false)
    setUnreadFromOtherRole(0)
  }, [selectedUserRole])

  // Process messages when they change
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!messagesLoading) {
      if (messages.length > 0 && selectedChat) {
        const otherPartyId = selectedUserRole == "traveller" ? selectedChat.travellerId : selectedChat.agentId
        const formattedMessages: IChatDetail[] = messages.map((message) => ({
          senderId: message.senderId,
          senderName:
            message.senderId === otherPartyId
              ? selectedUserRole == "traveller"
                ? selectedChat.travellerName
                : selectedChat.agentName
              : "Support",
          text: message.text,
          timestamp: new Date(message.timestamp).toISOString(),
          sortingTimeStamp: message.sortingTimeStamp,
          read: message.read,
          imageURL: message.imageURL || undefined,
        }))

        setChatDetail(formattedMessages)
        markMessagesAsRead()
      } else if (!messagesLoading) {
        setChatDetail([])
      }
      timer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [messages, messagesLoading, selectedChat, currentUserId])

  // Scroll to bottom when chat details change
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (chatDetailContainerRef.current) {
        chatDetailContainerRef.current.scrollTop = chatDetailContainerRef.current.scrollHeight;

      }
    }, 0);

    return () => clearTimeout(timeout);
  }, [chatDetail, loading]);


  const getSasToken = () => {
    getSasTokenMutation.mutate({});
  }

  const getSasTokenMutation = useGetSasToken({
    onSuccess: (response: IResponse) => {
      if (response?.data != null) {
        setSasToken(response.data);
        SetIsSasLoaded(true);
        message.success(response.statusMessage);
        return;
      }
      message.error(response.statusMessage);
      SetIsSasLoaded(true);
    },
    onError: (error) => {
      message.error(error.message);
      SetIsSasLoaded(true);
    }
  });


  const fetchUsersForNewChatMutation = useGetUsersForNewChat({
    onSuccess: (response: IResponse) => {
      if (response.statusCode === 200 && response?.data) {
        setNewUsers(response.data)
        setLoadingNewUsers(false)
      } else {
        message.error("Error fetching user chat details.")
      }
    },
    onError: (error: any) => {
      message.error(error.message)
    },
  })

  const fetchAdminChatListMutation = useFetchAdminChatList({
    onSuccess: (response: IResponse) => {
      if (response.statusCode === 200 && response?.data) {

        const formattedData = response.data.chatHistory.map((data: any) => {
          const agentImageUrl = data.agentImageUrl
            ? `${data.agentImageUrl}?${sasToken}`
            : null;
          const travellerImageUrl = data.travellerImageUrl ? `${data.travellerImageUrl}?${sasToken}` : null;
          return {
            ...data,
            agentImageUrl,
            travellerImageUrl
          }
        })
        setChatList(formattedData)
      } else {
        message.error("Error fetching user chat details.")
      }
      setLoadingAdminMessages(false)
    },
    onError: (error: any) => {
      message.error(error.message)
      setLoadingAdminMessages(false)
    },
  })

  const fetchOtherRoleChatListMutation = useFetchAdminChatList({
    onSuccess: (response: IResponse) => {
      if (response.statusCode === 200 && response?.data) {

        const unreadCount = response.data.userUnreadCount || 0
        setUnreadFromOtherRole(unreadCount)
      }
    },
    onError: (error: any) => {
      console.error("Error fetching other role chat list:", error)
    },
  })

  const fetchChatList = (searchFilter: string) => {
    fetchAdminChatListMutation.mutate({
      searchFilter: searchFilter,
      userFlag: selectedUserRole,
    })
  }

  const fetchOtherRoleUnreadMessages = (otherRole: string) => {
    fetchOtherRoleChatListMutation.mutate({
      searchFilter: "",
      userFlag: otherRole,
    })
  }


  // Mark messages as read
  const markMessagesAsRead = async () => {
    if (!selectedChat) return

    try {
      const chatRef = doc(db, chatCollectionModel.adminChats!, selectedChat.chatId)
      await updateDoc(chatRef, {
        [`unreadCount.${currentUserId}`]: 0,
      })
    } catch (error) {
      console.error("Error marking messages as read:", error)
    }
  }

  // Handle chat selection
  const handleChatSelection = (chat: IChatList) => {
    setSelectedChat(chat)
    setIsLoading(true)
    setUploadedImages([])

    if (chat && chat.chatId) {
      try {
        const chatRef = doc(db, chatCollectionModel.adminChats!, chat.chatId)
        updateDoc(chatRef, {
          [`unreadCount.${currentUserId}`]: 0,
        })
      } catch (error) {
        console.error("Error marking messages as read:", error)
      }
    }
  }

  // Handle search with debouncing
  useEffect(() => {
    if (searchNewUsersValue.trim()) {
      const searchTimeout = setTimeout(() => {
        fetchNewUserList(searchNewUsersValue)
      }, 500)

      return () => clearTimeout(searchTimeout)
    } else {
      // When search is cleared, get all data
      fetchNewUserList("")
    }
  }, [searchNewUsersValue])

  const fetchNewUserList = (value: string) => {
    fetchUsersForNewChatMutation.mutate({
      searchFilter: value,
      userFlag: selectedUserRole,
    })
  }

  const handleImageUpload = async ({ fileList }: { fileList: UploadFile[] }) => {
    if (fileList.length > 0) {
      setUploadingImage(true)
      for (const file of fileList) {
        if (file.originFileObj && !file.url) {
          const fileExtension = file.originFileObj.name.split(".").pop()
          const fileName = "chat_image_" + new Date().getTime() + "." + fileExtension

          try {
            const blobServiceClient = new BlobServiceClient(
              `https://${azureBlobStorageConfig.accountName}.blob.core.windows.net/?${sasToken}`,
            )

            const containerClient = blobServiceClient.getContainerClient(azureBlobStorageConfig.containerName as string)

            const blobPath = "temp/" + fileName
            const blockBlobClient = containerClient.getBlockBlobClient(blobPath)

            await blockBlobClient.uploadBrowserData(file.originFileObj, {
              blobHTTPHeaders: { blobContentType: file.originFileObj.type },
            })

            const fileUrl = blockBlobClient.url
            file.url = fileUrl

            const uploadedImageFile: UploadFile = {
              uid: file.uid,
              name: file.name || "Chat Image",
              status: "done",
              url: fileUrl,
            }

            setUploadedImages([uploadedImageFile])
            setUploadingImage(false)
          } catch (error: any) {
            message.error(error.message || "Failed to upload image. Please try again!")
            setUploadingImage(false)
            return false
          }
        }
      }
    }
  }

  const handleDeleteImage = async (file: UploadFile) => {
    if (file && file.url) {
      const fileName = file.url.split("/").pop()
      if (fileName) {
        try {
          const blobServiceClient = new BlobServiceClient(
            `https://${azureBlobStorageConfig.accountName}.blob.core.windows.net/?${sasToken}`,
          )

          const containerClient = blobServiceClient.getContainerClient(azureBlobStorageConfig.containerName as string)

          const blobPath = "chat-images/" + fileName
          const blockBlobClient = containerClient.getBlockBlobClient(blobPath)
          await blockBlobClient.deleteIfExists()
        } catch (error: any) {
          message.error("Error deleting image: " + error.message)
          return false
        }
      }
    }
    setUploadedImages([])
  }

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && uploadedImages.length === 0) || !selectedChat) return

    try {
      const chatId = selectedChat.chatId
      const messageText = messageInput.trim()
      let imageURL = uploadedImages.length > 0 ? uploadedImages[0].url : undefined

      setMessageInput("")
      setUploadedImages([])

      const receiverId = selectedUserRole == "traveller" ? selectedChat.travellerId : selectedChat.agentId
      if (currentUserId) {
        if (imageURL && imageURL.includes("temp")) {
          const movedUrl = await moveBlobInStorage(imageURL, "ChatImages", sasToken, azureBlobStorageConfig)
          if (movedUrl) {
            imageURL = movedUrl
          } else {
            message.error("Image upload failed while moving")
            return
          }
        }
        const senderName = "Support"
        const success = await FirestoreDatabaseService.sendMessage(
          chatId,
          currentUserId,
          receiverId,
          messageText || "",
          imageURL,
          senderName,
        )

        if (!success) {
          message.error("Failed to send message")
        }
      }
    } catch (error) {
      console.error("Error sending message:", error)
      message.error("Failed to send message")
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const month = months[date.getMonth()]
    const day = date.getDate()
    const year = date.getFullYear()

    let hours = date.getHours()
    const minutes = date.getMinutes().toString().padStart(2, "0")
    const ampm = hours >= 12 ? "PM" : "AM"

    hours = hours % 12
    hours = hours ? hours : 12

    return `${month} ${day}, ${year}, ${hours}:${minutes} ${ampm}`
  }

  const handleRoleChange = (event: any) => {
    setLoadingAdminMessages(true)
    setSelectedUserRole(event)
  }

  const handleSwitchToOtherRole = () => {
    setLoadingAdminMessages(true)
    const otherRole = selectedUserRole === "traveller" ? "attendant" : "traveller"
    setSelectedUserRole(otherRole)

  }

  const handleNewChat = () => {
    setShowNewChatModal(true)
    setLoadingNewUsers(true)
    fetchUsersForNewChatMutation.mutate({
      searchFilter: "",
      userFlag: selectedUserRole,
    })
    setSearchNewUsersValue("")
  }

  const onClose = () => {
    setShowNewChatModal(false)
  }

  const CreateChat = async (userId: string) => {
    setCreatingNewChat(userId)
    setLoadingAdminMessages(true)
    const currentUser = storage.getUserId()

    if (userId && currentUser) {
      try {
        const chatId = await FirestoreDatabaseService.getOrCreateChatId(currentUser, userId, true)

        if (chatId) {
          setShowNewChatModal(false)
          fetchChatList("")

          setTimeout(() => {
            const newChat = chatList.find((chat) => chat.chatId === chatId)
            if (newChat) {
              setSelectedChat(newChat)
            } else {
              const checkForNewChat = setInterval(() => {
                const foundChat = chatList.find((chat) => chat.chatId === chatId)
                if (foundChat) {
                  setSelectedChat(foundChat)
                  clearInterval(checkForNewChat)
                }
              }, 500)

              setTimeout(() => clearInterval(checkForNewChat), 5000)
            }
            setCreatingNewChat(null)
          }, 1000)
        } else {
          setCreatingNewChat(null)
          setLoadingAdminMessages(false)
          message.error("Failed to create chat")
        }
      } catch (error) {
        console.error("Error creating chat:", error)
        setCreatingNewChat(null)
        setLoadingAdminMessages(false)
        message.error("Failed to create chat")
      }
    } else {
      setCreatingNewChat(null)
      setLoadingAdminMessages(false)
    }
  }

  const clearSearch = () => {
    setSearchValue("")
  }

  const getOtherRoleName = () => {
    return selectedUserRole === "traveller" ? "attendant" : "traveller"
  }

  const selectedChatOtherPartyId = selectedChat
    ? selectedUserRole === "traveller"
      ? selectedChat.travellerId
      : selectedChat.agentId
    : undefined

  return (
    <>
      <div className="p-0 pb-2 border-b flex justify-start">
        <div style={{ width: "200px" }}>
          <Select
            value={selectedUserRole}
            style={{ width: "100%" }}
            onChange={handleRoleChange}
            options={[
              { value: "traveller", label: "Traveller" },
              { value: "attendant", label: "Attendant" },
            ]}
          />
        </div>
      </div>

      {unreadFromOtherRole > 0 && (
        <div
          className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={handleSwitchToOtherRole}
        >
          <div className="flex items-center">
            <div className="flex">
              <BellOutlined className="text-blue-400 mr-2" />
              <Badge count={unreadFromOtherRole} className="mr-2">
                <div className="w-4 h-4"></div>
              </Badge>
            </div>
            <div className="ml-2">
              <p className="text-blue-700 font-medium">
                You have {unreadFromOtherRole} unread message{unreadFromOtherRole > 1 ? "s" : ""} from{" "}
                {getOtherRoleName()}
              </p>
              <p className="text-blue-600 text-sm">Click here to view messages</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex bg-white" style={{ height: "80vh" }}>
        {/* Chat list */}
        <div className="w-80 border-r flex flex-col">
          <div className="flex items-center justify-between m-3">
            <h2 className="text-xl font-bold">Messages</h2>
            {NewChatAdd && (
              <Button type="primary" className="flex items-center bg-primary bg-[#1e3a8a]" onClick={handleNewChat}>
                <span className="mr-1">+</span> New Chat
              </Button>
            )}
          </div>
          <div className="p-4 flex-1 overflow-auto">
            <div className="relative mb-4">
              <Input
                placeholder="Search user by name"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.45)" }} />}
                suffix={
                  searchValue && (
                    <Button type="text" size="small" onClick={clearSearch} style={{ padding: 0, minWidth: "auto" }}>
                      ×
                    </Button>
                  )
                }
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  border: "none",
                  padding: "8px 12px",
                }}
                variant="borderless"
              />
            </div>

            {loadingAdminMessages ? (
              <div className="flex justify-center items-center h-40">
                <Spin />
              </div>
            ) : chatList.length == 0 ? (
              <div className="flex justify-center items-center h-40 text-gray-500">No users found</div>
            ) : (
              <div className="space-y-2">
                {chatList.map((chat) => (
                  <div
                    key={chat.chatId}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${selectedChat?.chatId === chat.chatId ? "bg-[#1e3a8a] text-white" : "hover:bg-gray-100"
                      }`}
                    onClick={() => handleChatSelection(chat)}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        {chat.travellerImageUrl || chat.agentImageUrl ? (
                          <Avatar src={selectedUserRole == "traveller" ? chat.travellerImageUrl : chat.agentImageUrl} size={40} className="bg-gray-200" />
                        ) : (
                          <Avatar className="bg-gray-200" size={40}>
                            {selectedUserRole == "traveller" ? chat.travellerName.charAt(0).toUpperCase() : chat.agentName.charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium truncate">{chat.travellerName || chat.agentName}</span>
                          {chat.chatId && <UnreadBadge chatId={chat.chatId} userId={currentUserId ?? ""} />}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat content */}
        <div className="flex-1 flex flex-col">
          {selectedChat ? (
            <>
              <div className="p-4 border-b bg-white flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {selectedChat.travellerImageUrl || selectedChat.agentImageUrl ? (
                    <Avatar src={selectedUserRole == "traveller" ? selectedChat.travellerImageUrl : selectedChat.agentImageUrl} size={40} className="bg-gray-200" />
                  ) : (
                    <Avatar className="bg-gray-200" size={40}>
                      {selectedUserRole == "traveller" ? selectedChat.travellerName.charAt(0).toUpperCase() : selectedChat.agentName.charAt(0).toUpperCase()}
                    </Avatar>
                  )}
                  <div>
                    <h2 className="text-lg font-semibold">{selectedChat.travellerName || selectedChat.agentName}</h2>
                  </div>
                </div>
                <div className="flex items-center space-x-2">

                </div>
              </div>

              <div
                ref={chatDetailContainerRef}
                className="flex-1 p-4 overflow-y-auto space-y-6"
                style={{ maxHeight: "calc(80vh - 140px)" }}
              >
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <Spin size="large" />
                  </div>
                ) : chatDetail.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-gray-500">No messages yet</div>
                ) : (
                  chatDetail.map((message, index) => (
                    <div key={`${message.senderId}-${message.timestamp}-${index}`} className="space-y-1">
                      {message.senderId !== selectedChatOtherPartyId && (
                        <div className="text-right">
                          <span className="text-sm font-medium">Support</span>
                        </div>
                      )}
                      {message.senderId === selectedChatOtherPartyId && (
                        <div>
                          <span className="text-sm font-medium">{message.senderName}</span>
                        </div>
                      )}
                      <div className={`flex ${message.senderId !== selectedChatOtherPartyId ? "justify-end" : "justify-start"} mb-2`}>
                        <div
                          className={`
                      max-w-[70%] rounded-lg overflow-hidden
                      ${message.imageURL && message.text
                              ? `p-1 ${message.senderId !== selectedChatOtherPartyId ? "bg-[#1e3a8a] text-white rounded-tr-none" : "bg-[#e8ebf2] text-gray-800 rounded-tl-none"}`
                              : message.text
                                ? `p-3  ${message.senderId !== selectedChatOtherPartyId ? "bg-[#1e3a8a] text-white rounded-tr-none" : "bg-[#e8ebf2] text-gray-800 rounded-tl-none"}`
                                : ""}
                    `}
                        >
                          {message.imageURL && (
                            <div className={`${message.text ? "mb-2" : ""}`}>
                              <Image
                                src={message.imageURL}
                                alt="Chat Image"
                                className={`rounded-lg max-w-full max-h-[220px] ${message.text ? "" : "border border-gray-300"} object-cover cursor-pointer`}
                              />
                            </div>
                          )}

                          {message.text?.trim() && (
                            <p className="text-sm leading-relaxed whitespace-pre-line">
                              {message.text}
                            </p>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-xs text-gray-500 ${message.senderId !== selectedChatOtherPartyId ? "text-right" : "text-left"}`}
                      >
                        {formatDate(message.timestamp)}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="border-t bg-white">
                {uploadedImages.length > 0 && (
                  <div className="p-3 border-b bg-gray-50">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Image to send:</span>
                      <div className="relative">
                        <Image
                          src={uploadedImages[0].url || "/placeholder.svg"}
                          width={60}
                          height={60}
                          className="h-16 w-16 object-cover rounded-lg"
                        />
                        <Button
                          type="text"
                          size="small"
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center"
                          onClick={() => handleDeleteImage(uploadedImages[0])}
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {WriteMessage && (
                  <div className="border-t bg-white p-4">
                    <div className="flex items-center space-x-2">
                      <Input
                        placeholder="Start Typing"
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onPressEnter={handleSendMessage}
                        variant="borderless"
                        className="flex-1 bg-gray-50 rounded-lg px-4 py-2"
                      />

                      <Form form={form}>
                        <Form.Item noStyle>
                          <Upload
                            fileList={uploadedImages}
                            onChange={handleImageUpload}
                            onRemove={handleDeleteImage}

                            accept="image/*"
                            maxCount={1}
                            showUploadList={false}
                          >
                            <Button
                              type="text"
                              className="mr-2"
                              icon={<PlusOutlined style={{ fontSize: "20px", color: "#1e3a8a" }} />}
                              loading={uploadingImage}
                              disabled={uploadedImages.length > 0}
                            />
                          </Upload>
                        </Form.Item>
                      </Form>

                      {(messageInput.trim() || uploadedImages.length > 0) && (
                        <Button
                          type="primary"
                          shape="circle"
                          icon={<SendOutlined />}
                          onClick={handleSendMessage}
                          className="bg-[#1e3a8a] hover:bg-[#1e40af]"
                        />
                      )}

                    </div>
                  </div>
                )}
              </div>

            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select a conversation to start messaging
            </div>
          )}
        </div>
      </div>

      {showNewChatModal && (
        <Modal
          title="Create New Chat"
          open={true}
          onCancel={creatingNewChat ? undefined : onClose}
          footer={null}
          width={500}
          closable={!creatingNewChat}
          maskClosable={!creatingNewChat}
        >
          <div className="py-4">
            <Input
              placeholder="Search users by name or email"
              value={searchNewUsersValue}
              onChange={(e) => setSearchNewUsersValue(e.target.value)}
              prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.45)" }} size={16} />}
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                border: "none",
                padding: "8px 12px",
              }}
              className="mb-4"
              disabled={!!creatingNewChat}
              variant="borderless"
            />
            {loadingNewUsers ? (
              <div className="flex justify-center items-center h-full">
                <Spin />
              </div>
            ) : newUsers.length == 0 ? (
              <div className="flex justify-center items-center h-full text-gray-500">No users found</div>
            ) : (
              <div className="space-y-4 h-80 overflow-y-auto">
                {newUsers.map((user) => (
                  <div key={user.userId} className="flex items-center justify-between border rounded-md p-3">
                    <div className="flex items-center">
                      {user.userImageUrl ? (
                        <Avatar src={user.userImageUrl} className="mr-3" />
                      ) : (
                        <Avatar className="mr-3 bg-gray-300">
                          {user.userName ? user.userName.charAt(0).toUpperCase() : "U"}
                        </Avatar>
                      )}
                      <div>
                        <div className="font-medium">{user.userName}</div>
                        <div className="text-sm text-gray-500">{user.userEmail}</div>
                      </div>
                    </div>
                    <Button
                      type="primary"
                      className={`${creatingNewChat
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-blue-900 hover:bg-blue-800 cursor-pointer"
                        }`}
                      onClick={() => CreateChat(user.userId ?? "")}
                      loading={creatingNewChat === user.userId}
                      disabled={!!creatingNewChat}
                    >
                      {creatingNewChat === user.userId ? "Creating..." : "Start Chat"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  )
}

// Component to show unread message count
function UnreadBadge({ chatId, userId }: { chatId: string; userId: string }) {
  const [value, loading, error] = useDocument(doc(db, chatCollectionModel.adminChats!, chatId), {
    snapshotListenOptions: { includeMetadataChanges: true },
  })

  if (loading) return null
  if (error) return null
  const data = value?.data()
  const unreadCount = data?.unreadCount?.[userId] || 0

  if (unreadCount === 0) return null

  return (
    <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
      {unreadCount > 9 ? "9+" : unreadCount}
    </span>
  )
}
